"use strict";
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
exports.__esModule = true;
exports.makeOpenedHistoryForAccounts = exports.makeClosedHistoryForAccounts = exports.makeCompoundSummaryForAccount = exports.getEnablingOp = exports.getAccountCapabilities = void 0;
var bignumber_js_1 = require("bignumber.js");
var currencies_1 = require("../currencies");
var compound_1 = require("../families/ethereum/modules/compound");
// to confirm in practice if this threshold is high enough / too high
var unlimitedThreshold = new bignumber_js_1.BigNumber(2).pow(250);
function getAccountCapabilities(account) {
    if (account.balance.eq(0))
        return;
    var token = account.token;
    var ctoken = (0, currencies_1.findCompoundToken)(token);
    if (!ctoken) {
        return {
            enabledAmount: new bignumber_js_1.BigNumber(0),
            enabledAmountIsUnlimited: false,
            canSupply: false,
            canSupplyMax: false,
            canWithdraw: false,
            status: null
        };
    }
    var enabledAmount = (0, compound_1.getEnabledAmount)(account);
    var enabledAmountIsUnlimited = enabledAmount.gt(unlimitedThreshold);
    // TODO: we might want to keep things to false if there are pending actions
    var canSupply = enabledAmount.gt(0) && account.spendableBalance.gt(0);
    var canSupplyMax = canSupply && account.spendableBalance.lte(enabledAmount);
    var cdaiBalanceInDai = account.balance.minus(account.spendableBalance);
    var canWithdraw = cdaiBalanceInDai.gt(0);
    var notEnabled = enabledAmount.eq(0);
    var pendingOps = account.pendingOperations.filter(function (op) { return !account.operations.some(function (o) { return o.hash === op.hash; }); });
    var enabling = pendingOps.some(function (op) { var _a; return (_a = op.extra) === null || _a === void 0 ? void 0 : _a.approving; });
    var supplying = pendingOps.some(function (op) { return op.type === "SUPPLY"; });
    var status;
    if (canWithdraw) {
        status = "EARNING";
    }
    else if (notEnabled) {
        if (enabling) {
            status = "ENABLING";
        }
        else {
            status = null;
        }
    }
    else if (supplying) {
        status = "SUPPLYING";
    }
    else {
        status = "INACTIVE";
    }
    return {
        enabledAmount: enabledAmount,
        enabledAmountIsUnlimited: enabledAmountIsUnlimited,
        // can supply at least the full amount of the balance
        canSupplyMax: canSupplyMax,
        // can supply anything at all
        canSupply: canSupply,
        // can withdraw anything at all
        canWithdraw: canWithdraw,
        status: status
    };
}
exports.getAccountCapabilities = getAccountCapabilities;
var getEnablingOp = function (account) {
    return account.pendingOperations.find(function (op) { var _a; return (_a = op.extra) === null || _a === void 0 ? void 0 : _a.approving; });
};
exports.getEnablingOp = getEnablingOp;
var calcInterests = function (value, openRate, closeOrCurrentRate) { return value.times(closeOrCurrentRate).minus(value.times(openRate)); };
function makeCompoundSummaryForAccount(account, parentAccount) {
    var capabilities = getAccountCapabilities(account);
    if (!capabilities)
        return;
    var status = capabilities.status;
    if (!status)
        return;
    var operations = account.operations;
    var opened = [];
    var closed = [];
    var totalSupplied = new bignumber_js_1.BigNumber(0);
    var allTimeEarned = new bignumber_js_1.BigNumber(0);
    var accruedInterests = new bignumber_js_1.BigNumber(0);
    var currentRate = (0, compound_1.findCurrentRate)(account.token);
    if (!currentRate) {
        return {
            opened: [],
            closed: [],
            account: account,
            parentAccount: parentAccount,
            totalSupplied: totalSupplied,
            allTimeEarned: allTimeEarned,
            accruedInterests: accruedInterests,
            status: status
        };
    }
    operations
        .slice(0)
        .reverse()
        .filter(function (op) { return !op.hasFailed; })
        .forEach(function (operation) {
        if (operation.type === "SUPPLY") {
            opened.push({
                startingDate: operation.date,
                amountSupplied: operation.value,
                openRate: new bignumber_js_1.BigNumber(operation.extra.rate),
                compoundValue: new bignumber_js_1.BigNumber(operation.extra.compoundValue)
            });
        }
        if (operation.type === "REDEEM") {
            var amountToClose = operation.value;
            while (amountToClose.gt(0) && opened.length > 0) {
                var closingOperation = opened.shift();
                // NOTE: Potentially not dangerous as we do check that
                // opened.length > 0 so unshift should never be undefined
                if (!closingOperation)
                    return;
                if (amountToClose.gte(closingOperation.amountSupplied)) {
                    closed.push({
                        amountSupplied: closingOperation.amountSupplied,
                        openRate: closingOperation.openRate,
                        closeRate: new bignumber_js_1.BigNumber(operation.extra.rate),
                        endDate: operation.date,
                        startingDate: closingOperation.startingDate,
                        compoundValue: new bignumber_js_1.BigNumber(operation.extra.compoundValue)
                    });
                }
                else {
                    closed.push({
                        amountSupplied: amountToClose,
                        openRate: closingOperation.openRate,
                        closeRate: new bignumber_js_1.BigNumber(operation.extra.rate),
                        endDate: operation.date,
                        startingDate: closingOperation.startingDate,
                        compoundValue: new bignumber_js_1.BigNumber(operation.extra.compoundValue)
                    });
                    opened.unshift({
                        amountSupplied: closingOperation.amountSupplied.minus(amountToClose),
                        openRate: closingOperation.openRate,
                        startingDate: closingOperation.startingDate,
                        compoundValue: closingOperation.compoundValue.minus(new bignumber_js_1.BigNumber(operation.extra.compoundValue))
                    });
                }
                amountToClose = amountToClose.minus(closingOperation.amountSupplied);
            }
        }
    });
    var o = opened.map(function (op) {
        var compoundValue = op.compoundValue, openRate = op.openRate, amountSupplied = op.amountSupplied, startingDate = op.startingDate;
        var interestsEarned = calcInterests(compoundValue, openRate, currentRate.rate);
        var percentageEarned = interestsEarned.div(amountSupplied).times(100);
        totalSupplied = totalSupplied.plus(amountSupplied);
        accruedInterests = accruedInterests.plus(interestsEarned);
        allTimeEarned = allTimeEarned.plus(interestsEarned);
        return {
            startingDate: startingDate,
            amountSupplied: amountSupplied,
            interestsEarned: interestsEarned,
            percentageEarned: percentageEarned.toNumber()
        };
    });
    var c = closed.map(function (op) {
        var compoundValue = op.compoundValue, openRate = op.openRate, amountSupplied = op.amountSupplied, startingDate = op.startingDate, endDate = op.endDate, closeRate = op.closeRate;
        var interestsEarned = calcInterests(compoundValue, openRate, closeRate);
        var percentageEarned = interestsEarned.div(amountSupplied).times(100);
        allTimeEarned = allTimeEarned.plus(interestsEarned);
        return {
            startingDate: startingDate,
            endDate: endDate,
            amountSupplied: amountSupplied,
            interestsEarned: interestsEarned,
            percentageEarned: percentageEarned.toNumber()
        };
    });
    return {
        account: account,
        parentAccount: parentAccount,
        opened: o,
        closed: c,
        totalSupplied: totalSupplied,
        allTimeEarned: allTimeEarned,
        accruedInterests: accruedInterests,
        status: status
    };
}
exports.makeCompoundSummaryForAccount = makeCompoundSummaryForAccount;
var makeClosedHistoryForAccounts = function (summaries) {
    return summaries
        .reduce(function (loans, summary) {
        if (!summary.closed.length)
            return loans;
        return loans.concat(summary.closed.map(function (c) { return (__assign(__assign({}, c), { account: summary.account, parentAccount: summary.parentAccount })); }));
    }, [])
        .sort(function (a, b) {
        return b.endDate.getTime() - a.endDate.getTime();
    });
};
exports.makeClosedHistoryForAccounts = makeClosedHistoryForAccounts;
var makeOpenedHistoryForAccounts = function (summaries) {
    return summaries
        .reduce(function (loans, summary) {
        if (!summary.opened.length)
            return loans;
        return loans.concat(
        // $FlowFixMe issue on LLD side
        summary.opened.map(function (c) { return (__assign(__assign({}, c), { account: summary.account, parentAccount: summary.parentAccount })); }));
    }, [])
        .sort(function (a, b) {
        return b.startingDate.getTime() - a.startingDate.getTime();
    });
};
exports.makeOpenedHistoryForAccounts = makeOpenedHistoryForAccounts;
//# sourceMappingURL=logic.js.map